/**
 * Capture transform for WidgetAnnotationExplorer.
 *
 * Two problems html2canvas hits on this widget that other widgets don't:
 *  1. Each AnnoCard embeds a Lexical contenteditable. html2canvas fails to
 *     clone the editor's internal DOM and ends up painting the wrapper's
 *     dark text color as a solid background — the cards render as black
 *     rectangles.
 *  2. The generic capture flow sets `min-width: max-content` on the outer
 *     container so charts can render past their scroll box. Inside Lexical,
 *     that removes the soft-wrap constraint and each paragraph tries to
 *     lay out on one line, blowing capture width past the 16384px canvas
 *     ceiling and producing the squashed billboard aspect ratio.
 *
 * Fix, scoped to this widget only: for every contenteditable inside the
 * container, hide it and insert a static mirror node (same innerHTML, same
 * computed text styles, no contenteditable) with a hard max-width + wrap
 * rules. After capture, the mirror is removed and the editor restored.
 */

const CARD_TEXT_MAX_WIDTH = 320;

export const prepareAnnotationExplorer = async (containerElement) => {
  const restorations = [];

  const editables = containerElement.querySelectorAll('[contenteditable]');
  editables.forEach((el) => {
    const cs = window.getComputedStyle(el);

    const mirror = document.createElement('div');
    mirror.innerHTML = el.innerHTML;
    mirror.setAttribute('data-anno-capture-mirror', '');
    mirror.style.font = cs.font;
    mirror.style.color = cs.color;
    mirror.style.lineHeight = cs.lineHeight;
    mirror.style.letterSpacing = cs.letterSpacing;
    mirror.style.maxWidth = `${CARD_TEXT_MAX_WIDTH}px`;
    mirror.style.whiteSpace = 'pre-wrap';
    mirror.style.overflowWrap = 'break-word';
    mirror.style.wordBreak = 'break-word';

    const prevDisplay = el.style.display;
    el.style.display = 'none';
    el.parentNode.insertBefore(mirror, el);

    restorations.push(() => {
      mirror.remove();
      el.style.display = prevDisplay;
    });
  });

  return () => {
    while (restorations.length) restorations.pop()();
  };
};
